import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  ChevronLeft, Download, Loader2, Save, Send, Sparkles, ShieldAlert,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ModuleTitleLIFT } from "@/components/lift/ModuleTitleLIFT";

export default function SynthesisPage() {
  const { cycleId, employeeId } = useParams();
  const navigate = useNavigate();
  const [packet, setPacket] = useState(null);
  const [summary, setSummary] = useState("");
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/lift/synthesis/${cycleId}/${employeeId}`);
      setPacket(data);
      setSummary(data.synthesis_summary || "");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cycleId, employeeId]);

  const generateDraft = async () => {
    setAiLoading(true);
    try {
      const { data } = await api.post(`/lift/synthesis/${cycleId}/${employeeId}/ai-draft`);
      setSummary(data.draft || "");
      toast.success("AI draft generated — please review and edit before finalizing.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setAiLoading(false);
    }
  };

  const saveSummary = async () => {
    setSaving(true);
    try {
      await api.patch(`/lift/synthesis/${cycleId}/${employeeId}`, {
        summary,
        reviewed_in_meeting: true,
      });
      toast.success("Summary saved.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const finalize = async () => {
    if (!summary.trim()) {
      toast.error("Please write or edit the synthesis summary before finalizing.");
      return;
    }
    if (!window.confirm("Finalize this packet and release it to the employee?")) return;
    setFinalizing(true);
    try {
      // Ensure the latest summary is stored first
      await api.patch(`/lift/synthesis/${cycleId}/${employeeId}`, {
        summary,
        reviewed_in_meeting: true,
      });
      await api.post(`/lift/synthesis/${cycleId}/${employeeId}/finalize`);
      toast.success("Packet finalized. The employee has been notified.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    } finally {
      setFinalizing(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/lift/synthesis/${cycleId}/${employeeId}/pdf`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `LIFT-${packet?.employee?.name || "packet"}.pdf`.replace(/\s+/g, "-");
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  if (!packet) return <p className="italic text-charcoal-muted">Loading synthesis…</p>;

  const agg = packet.peer_aggregate || {};
  const finalized = packet.status_synthesis === "finalized" || packet.status_synthesis === "acknowledged";

  return (
    <div className="space-y-6" data-testid="lift-synthesis-page">
      <Link to={`/lift/manager/${cycleId}`} className="patria-btn-ghost -ml-3">
        <ChevronLeft className="h-4 w-4" strokeWidth={1.5} /> Back to reports
      </Link>
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <ModuleTitleLIFT />
          <h1 className="mt-3 text-4xl font-serif text-forest-900">
            Synthesis · {packet.employee?.name}
          </h1>
          <p className="text-sm text-charcoal-muted">{packet.cycle_name}</p>
        </div>
        <div className="flex gap-2">
          <button className="patria-btn-secondary" onClick={downloadPdf} data-testid="synthesis-pdf">
            <Download className="h-4 w-4" strokeWidth={1.5} /> Download PDF
          </button>
          {!finalized && (
            <button className="patria-btn-primary" onClick={finalize} disabled={finalizing} data-testid="synthesis-finalize">
              <Send className="h-4 w-4" strokeWidth={1.5} />
              {finalizing ? "Finalizing..." : "Finalize & release"}
            </button>
          )}
        </div>
      </div>

      {finalized && (
        <div className="patria-card p-5 flex items-center gap-3">
          <span className="patria-chip-green">Released</span>
          <p className="text-sm text-charcoal-soft">
            Released {packet.packet_released_at}. {packet.acknowledgment?.timestamp
              ? `Employee acknowledged on ${packet.acknowledgment.timestamp}.`
              : "Awaiting employee acknowledgment."}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <PacketPanel title="1. Self-reflection">
          {packet.self_reflection?.length ? (
            packet.self_reflection.map((a) => (
              <AnswerRow key={a.question_id} a={a} />
            ))
          ) : (
            <p className="italic text-charcoal-muted">Not submitted yet.</p>
          )}
        </PacketPanel>

        <PacketPanel title="2. Manager evaluation">
          {packet.manager_eval?.answers?.map((a) => (
            <AnswerRow key={a.question_id} a={a} />
          ))}
          <LabelBlock label="Strengths" value={packet.manager_eval?.strengths} />
          <LabelBlock label="Opportunities" value={packet.manager_eval?.opportunities} />
        </PacketPanel>

        <PacketPanel title="3. Peer feedback (anonymized)" testId="synthesis-peer-agg">
          {agg.suppressed ? (
            <p className="italic text-terracotta-600">{agg.suppressed_note}</p>
          ) : (
            <div className="space-y-4">
              <p className="text-xs text-charcoal-muted">
                {agg.response_count} responses (min for employee view: {agg.min_required})
              </p>
              {agg.strengths_bullets?.length > 0 && (
                <div>
                  <p className="patria-chip-green">Strengths themes</p>
                  <ul className="mt-2 list-disc pl-5 text-sm text-charcoal-soft space-y-1">
                    {agg.strengths_bullets.map((b) => <li key={`s-${b}`}>{b}</li>)}
                  </ul>
                </div>
              )}
              {agg.growth_bullets?.length > 0 && (
                <div>
                  <p className="patria-chip-terra">Growth themes</p>
                  <ul className="mt-2 list-disc pl-5 text-sm text-charcoal-soft space-y-1">
                    {agg.growth_bullets.map((b) => <li key={`g-${b}`}>{b}</li>)}
                  </ul>
                </div>
              )}
              {agg.ratings?.length > 0 && (
                <div className="mt-3 overflow-hidden rounded-2xl shadow-card">
                  <table className="patria-table w-full">
                    <thead>
                      <tr><th>Question</th><th>Average</th><th>Responses</th></tr>
                    </thead>
                    <tbody>
                      {agg.ratings.map((r) => (
                        <tr key={r.question_id}>
                          <td>{r.question_text}</td>
                          <td>{r.avg}</td>
                          <td>{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </PacketPanel>

        <PacketPanel
          title="4. Manager's synthesis narrative"
          testId="synthesis-narrative"
          right={
            <button
              className="patria-btn-terracotta text-xs"
              onClick={generateDraft}
              disabled={aiLoading || finalized}
              data-testid="synthesis-ai-generate"
            >
              {aiLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" strokeWidth={1.5} />}
              {aiLoading ? "Drafting..." : "AI-assist (optional)"}
            </button>
          }
        >
          <textarea
            className="patria-input w-full min-h-[240px] font-serif text-base leading-relaxed"
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
            disabled={finalized}
            placeholder="Write a warm, honest summary that ties the pieces together. You always edit and finalize."
            data-testid="synthesis-summary-input"
          />
          {packet.synthesis_ai_generated && (
            <p className="mt-2 text-xs text-charcoal-muted italic">
              Draft was AI-assisted — please read carefully and edit to reflect your own voice.
            </p>
          )}
          {!finalized && (
            <div className="mt-3 flex justify-end">
              <button
                className="patria-btn-secondary"
                onClick={saveSummary}
                disabled={saving}
                data-testid="synthesis-save-summary"
              >
                <Save className="h-4 w-4" strokeWidth={1.5} /> Save summary
              </button>
            </div>
          )}
        </PacketPanel>
      </div>
    </div>
  );
}

function PacketPanel({ title, children, right, testId }) {
  return (
    <section className="patria-card p-6" data-testid={testId}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="text-xl font-serif text-forest-900">{title}</h3>
        {right}
      </div>
      {children}
    </section>
  );
}

function AnswerRow({ a }) {
  return (
    <div className="mb-3">
      <p className="text-xs uppercase tracking-widest text-charcoal-muted">{a.question_text}</p>
      <p className="text-sm text-charcoal whitespace-pre-wrap">
        {a.type === "rating_5" ? `${a.value || 0} / 5` : (a.value || <span className="italic text-charcoal-muted">not answered</span>)}
      </p>
    </div>
  );
}

function LabelBlock({ label, value }) {
  return (
    <div className="mt-3">
      <p className="text-xs uppercase tracking-widest text-charcoal-muted">{label}</p>
      <p className="text-sm text-charcoal whitespace-pre-wrap">{value || <span className="italic text-charcoal-muted">(none)</span>}</p>
    </div>
  );
}
